import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import { withTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { Tooltip } from "reactstrap";
import ReactDatePickerCommon from "components/ReactDatePickerCommon";
import CustomDropdown from "components/Dropdown";
import Loader from "components/Loader";
import tooltipImg from "assets/images/info_black-tooltip.svg";
import officePolicyStyles from "../../OfficePolicyManagement.module.scss";
import styles from "./../../../../../pages/CredentialsAndLicensing/Credentials.module.scss";
import constants from "../../../../../constants";
import { decodeId, parseCalendarDateToLocalDate } from "utils";
import { useQuery, useQueryClient } from "react-query";
import toast from "react-hot-toast";
import {
  getPolicyFolderById,
  createPolicy,
  getPolicyById,
  updatePolicy,
  usePolicyAcknowledgementTemplate,
} from "repositories/office-policy-management-repository";
import { useAllActiveOffices } from "repositories/scheduler-repository";
import useUploadService from "hooks/useUploadService";
import useManageOfficePolicyController from "../../hooks/useManageOfficePolicyController";
import SelectCategoryModal from "../../Modals/SelectCategoryModal";
import SelectOfficesModal from "../../Modals/SelectOfficesModal";
import InviteesPopup from "accountOwner/pages/EventDetails/components/Modals/InviteesPopup";
import SelectEmployeeForPolicyVersionModal from "../../../AddEvent/components/SelectEmployeeForPolicyVersionModal";
import ToggleSwitch from "components/ToggleSwitch";
import SelectPolicyApproverModal from "../../Modals/SelectPolicyApproverModal";
import Text from "components/Text";
import { scrollToError } from "../../../../../utils";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
const {
  TITLE_MAX_LENGTH,
  DOCUMENT_NAME_MAX,
  DESCRIPTION_MAX,
  NOTES_MAX,
  VERSION_MAX,
  MAX_FILES,
  MAX_URLS,
  OFFICE_DISPLAY_COUNT,
  PRIORITY_LOW,
  PRIORITY_MEDIUM,
  PRIORITY_HIGH,
  REMIND_DAYS,
} = constants.addNewPolicyLimits;

function mapFolderOfficesToForm(offices) {
  if (!Array.isArray(offices) || offices.length === 0) return [];
  return offices
    .map((o) => ({
      id: o.officeId,
      name: o.officeName ?? "",
      officeName: o.officeName ?? "",
    }))
    .filter((o) => o.id != null);
}

/** Map policy detail assignee to form/modal shape (preserves assignment id for publish API). */
function mapPolicyAssigneeToForm(assignee) {
  const userId = assignee?.user?.id ?? assignee?.userId;
  const officeId = assignee?.officeId;
  return {
    id: assignee?.id ?? 0,
    userId,
    firstName: assignee?.user?.firstName ?? assignee?.firstName ?? "",
    lastName: assignee?.user?.lastName ?? assignee?.lastName ?? "",
    profilePic: assignee?.user?.profilePic ?? assignee?.profilePic,
    ownerId: assignee?.ownerId,
    officeId,
    isOwner: Number(userId) === Number(assignee?.ownerId),
    offices: officeId != null ? [{ officeId }] : [],
  };
}

function isValidUrl(str) {
  if (!str || typeof str !== "string") return false;
  const trimmed = str.trim();
  if (!trimmed) return false;
  const normalized = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
  try {
    const parsed = new URL(normalized);
    return (
      ["http:", "https:"].includes(parsed.protocol) &&
      !!parsed.hostname &&
      parsed.hostname.includes(".")
    );
  } catch (e) {
    return false;
  }
}

const ChevronUpIcon = () => (
  <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
    <path
      d="M1 6.5L6 1.5L11 6.5"
      stroke="#587E85"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
    <path
      d="M1 1.5L6 6.5L11 1.5"
      stroke="#587E85"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TrashIcon = ({ onClick }) => (
  <span
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => e.key === "Enter" && onClick(e)}
  >
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#EE4F4F"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ cursor: "pointer" }}
    >
      <polyline points="3 6 5 6 21 6"></polyline>
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
    </svg>
  </span>
);

const CustomCheckbox = ({ label, checked, onChange, disabled }) => (
  <label
    className={
      officePolicyStyles["checkbox-label"] +
      " ch-checkbox c-field " +
      (disabled ? "disabled" : "")
    }
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      className={officePolicyStyles["checkbox-input"]}
    />
    <span className={officePolicyStyles["checkbox-text"]}>{label}</span>
  </label>
);

const SectionCard = ({ title, isOpen, onToggle, children }) => (
  <div className={officePolicyStyles["section-card"]}>
    <div
      className={officePolicyStyles["section-header-row"]}
      onClick={onToggle}
    >
      <h3 className={officePolicyStyles["section-title"]}>{title}</h3>
      {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
    </div>
    {isOpen && (
      <div className={officePolicyStyles["section-body"]}>{children}</div>
    )}
  </div>
);

function getStartOfDay(d) {
  const date = d ? new Date(d) : new Date();
  date.setHours(0, 0, 0, 0);
  return date;
}

function daysBetween(start, end) {
  const s = getStartOfDay(start);
  const e = getStartOfDay(end);
  return Math.max(0, Math.ceil((e - s) / (24 * 60 * 60 * 1000)));
}

function daysFromToday(date) {
  return daysBetween(new Date(), date);
}

const POLICY_DOCUMENTS_CONTAINER = "policy-documents";

const AddnewPolicyDocument = ({ t }) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { officePolicyFolderId, policyId: policyIdParam } = useParams();
  const folderIdDecoded = decodeId(officePolicyFolderId);
  const policyIdDecoded = decodeId(policyIdParam);
  const isEditMode = !!policyIdDecoded;
  const fileInputRef = useRef(null);
  const { upload: uploadImage } = useUploadService();
  const {
    effectiveOwnerId: ownerId,
    getIdToCheckOwnerChange,
    selectedOwnerIdString,
  } = useAccountOwnerCommon();
  const {
    data: { categoryList, refetchCategories, accountOwnerId },
  } = useManageOfficePolicyController(t, history, false, undefined, () => {}, {
    isCategoryModalOpen: true,
  });

  const { data: acknowledgementTemplateData } =
    usePolicyAcknowledgementTemplate(accountOwnerId ?? null, {
      enabled: !!accountOwnerId,
    });
  const isAcknowledgementTemplateActive =
    acknowledgementTemplateData?.data?.isActive === true;
  const isApproverEnabledInTemplate =
    acknowledgementTemplateData?.data?.isApprovedByEnabled === true;
  const hasAcknowledgementTemplateData =
    acknowledgementTemplateData?.data !== undefined;

  useEffect(() => {
    if (getIdToCheckOwnerChange()) {
      history.push(
        constants.routes.officePolicyManagements.officePolicyManagement,
      );
    }
  }, [ownerId]);
  // Template approvers as staff-like list for Select Approver modal (reference: backup getApproversAsStaffList)
  const getApproversAsStaffList = useCallback(() => {
    const approvers =
      acknowledgementTemplateData?.data?.policyAcknowledgementApprovers;
    if (!Array.isArray(approvers)) return [];
    return approvers
      .filter((a) => !a.isDeleted)
      .map((approver) => {
        if (approver.approver) {
          return {
            ...approver.approver,
            title: approver.title || approver.approver.designation?.name || "",
            designation: approver.approver.designation || {
              id: null,
              name: approver.title || "",
            },
          };
        }
        if (approver.approverName) {
          return {
            id: approver.approverId ?? approver.id,
            firstName: (approver.approverName || "").split(/\s+/)[0] || "",
            lastName:
              (approver.approverName || "").split(/\s+/).slice(1).join(" ") ||
              "",
            profilePic: "",
            title: approver.title || "",
            designation: { id: null, name: approver.title || "" },
            isManualApprover: true,
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [acknowledgementTemplateData?.data?.policyAcknowledgementApprovers]);

  const getDefaultApprover = useCallback(() => {
    const approvers =
      acknowledgementTemplateData?.data?.policyAcknowledgementApprovers;
    if (!Array.isArray(approvers)) return null;
    const defaultApprover = approvers.find(
      (a) => a.isDefaultApprover && !a.isDeleted,
    );
    if (!defaultApprover) return null;
    if (defaultApprover.approver) {
      return {
        ...defaultApprover.approver,
        title:
          defaultApprover.title ||
          defaultApprover.approver.designation?.name ||
          "",
        designation: defaultApprover.approver.designation || {
          id: null,
          name: "Unknown Role",
        },
      };
    }
    if (defaultApprover.approverName) {
      return {
        id: defaultApprover.approverId ?? defaultApprover.id,
        firstName: (defaultApprover.approverName || "").split(/\s+/)[0] || "",
        lastName:
          (defaultApprover.approverName || "")
            .split(/\s+/)
            .slice(1)
            .join(" ") || "",
        profilePic: "",
        title: defaultApprover.title || "",
        designation: { id: null, name: defaultApprover.title || "" },
        isManualApprover: true,
      };
    }
    return null;
  }, [acknowledgementTemplateData?.data?.policyAcknowledgementApprovers]);

  const formatApproverDisplayName = useCallback((approver) => {
    if (!approver) return null;
    const first = approver.firstName ?? approver.FirstName ?? "";
    const last = approver.lastName ?? approver.LastName ?? "";
    const title = approver.title ?? approver.designation?.name ?? "";
    return `${first} ${last}`.trim() + (title ? ` (${title})` : "");
  }, []);

  // Set approver when template loads: explicit default if any, else first listed approver (add mode only).
  useEffect(() => {
    const defaultApprover = getDefaultApprover();
    const staffList = getApproversAsStaffList();
    const initialApprover = defaultApprover ?? staffList[0] ?? null;
    if (
      initialApprover &&
      !selectedApprover &&
      !isEditMode &&
      isAcknowledgementTemplateActive &&
      isApproverEnabledInTemplate
    ) {
      setSelectedApprover(initialApprover);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run only when template/approvers or edit mode changes
  }, [
    acknowledgementTemplateData?.data?.policyAcknowledgementApprovers,
    isEditMode,
    isAcknowledgementTemplateActive,
    isApproverEnabledInTemplate,
  ]);

  const { data: folderData } = useQuery(
    ["/PolicyFolder", folderIdDecoded],
    () => getPolicyFolderById(folderIdDecoded),
    { enabled: !!folderIdDecoded && !isEditMode },
  );
  useEffect(() => {
    if (folderData?.ownerId && folderData?.ownerId !== ownerId) {
      history.push(
        constants.routes.officePolicyManagements.officePolicyManagement,
      );
    }
  }, [ownerId]);
  const {
    data: policyData,
    isLoading,
    isFetching,
  } = useQuery(
    ["policy", policyIdDecoded],
    () => getPolicyById(policyIdDecoded),
    { enabled: isEditMode && !!policyIdDecoded },
  );

  const policy =
    policyData && typeof policyData === "object" ? policyData : null;

  const isPolicyDraft = !!(policy?.isDraft ?? policy?.IsDraft);
  const lockAcknowledgementApproverFields = isEditMode && !isPolicyDraft;

  const { data: allOfficesData } = useAllActiveOffices(
    1,
    constants.ACTIVE_OFFICES_PAGE_SIZE,
    accountOwnerId ?? null,
    "",
    { enabled: !!accountOwnerId },
  );

  const [formPrefilled, setFormPrefilled] = useState(false);
  const [formPrefilledFromPolicy, setFormPrefilledFromPolicy] = useState(false);

  // Reset prefill flag when policy id or policy data changes so edit form shows updated data
  useEffect(() => {
    if (!isEditMode) return;
    setFormPrefilledFromPolicy(false);
  }, [isEditMode, policyIdDecoded, policy]);

  const [sectionsOpen, setSectionsOpen] = useState({
    policyDetails: true,
    documentDetails: true,
    assignees: true,
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Policy Details
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedOffices, setSelectedOffices] = useState({});
  const [selectedOfficeList, setSelectedOfficeList] = useState([]);
  const [isAllOffice, setIsAllOffice] = useState(false);
  const [startDate, setStartDate] = useState(() => getStartOfDay(new Date()));
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [officesModalOpen, setOfficesModalOpen] = useState(false);
  const [officeListPopupOpen, setOfficeListPopupOpen] = useState(false);
  const [assigneesPopupOpen, setAssigneesPopupOpen] = useState(false);
  const [managerMembersPopupOpen, setManagerMembersPopupOpen] = useState(false);
  const [invitePopupPosition, setInvitePopupPosition] = useState({
    top: 0,
    right: 0,
  });

  // Document Details
  const [policyDocumentName, setPolicyDocumentName] = useState("");
  const [notes, setNotes] = useState("");
  const [versionNumber, setVersionNumber] = useState("");
  const [attachedFiles, setAttachedFiles] = useState([]); // { path, file, Id, IsDeleted }[]
  const [urls, setUrls] = useState([{ id: 0, filePath: "", isDeleted: false }]);
  const filteredUrls = useMemo(() => {
    return urls.filter((urlItem) => !urlItem?.isDeleted && !urlItem?.IsDeleted);
  }, [urls]);
  // Policy acknowledgement (after URL field)
  const [
    allowPolicyAcknowledgementByStaff,
    setAllowPolicyAcknowledgementByStaff,
  ] = useState(true);
  const [includeApprover, setIncludeApprover] = useState(true);
  const [selectedApprover, setSelectedApprover] = useState(null);
  const [selectApproverModalOpen, setSelectApproverModalOpen] = useState(false);

  // Assignees
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return getStartOfDay(d);
  });
  const [priority, setPriority] = useState(PRIORITY_MEDIUM);
  const [assignees, setAssignees] = useState([]);
  const [notifyAssignees, setNotifyAssignees] = useState(false);
  const [notifyAssignor, setNotifyAssignor] = useState(false);
  const [notifyManagement, setNotifyManagement] = useState(false);
  const [managementOption, setManagementOption] = useState("all");
  const [selectedManagerUsers, setSelectedManagerUsers] = useState([]);
  const [remindAfterPublish, setRemindAfterPublish] = useState(false);
  const [remindAfterPublishDays, setRemindAfterPublishDays] = useState(1);
  const [remindBeforeDueDate, setRemindBeforeDueDate] = useState(false);
  const [remindBeforeDueDateDays, setRemindBeforeDueDateDays] = useState(1);
  const [createTask, setCreateTask] = useState(false);
  const [staffModalOpen, setStaffModalOpen] = useState(false);
  const [managerModalOpen, setManagerModalOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState({
    notifyAssignees: false,
    notifyManagement: false,
    remindAfterPublish: false,
    remindBeforeDueDate: false,
    createTask: false,
  });

  const toggleTooltip = useCallback((key) => {
    setTooltipOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  /** When the template has no default approver, show the current selection first in Select Approver modal. */
  const approverStaffListForModal = useMemo(() => {
    const list = getApproversAsStaffList();
    if (getDefaultApprover() || !selectedApprover || !list.length) {
      return list;
    }
    const selId = selectedApprover.id ?? selectedApprover.userId;
    let idx = -1;
    if (selId != null) {
      idx = list.findIndex(
        (item) =>
          (item.id ?? item.userId) != null &&
          (item.id ?? item.userId) === selId,
      );
    }
    if (idx < 0) {
      const selFirst = (
        selectedApprover.firstName ??
        selectedApprover.FirstName ??
        ""
      ).trim();
      const selLast = (
        selectedApprover.lastName ??
        selectedApprover.LastName ??
        ""
      ).trim();
      const selName = `${selFirst} ${selLast}`.toLowerCase();
      idx = list.findIndex((item) => {
        const n =
          `${(item.firstName ?? "").trim()} ${(item.lastName ?? "").trim()}`.toLowerCase();
        return n === selName;
      });
    }
    if (idx <= 0) return list;
    const next = [...list];
    const [row] = next.splice(idx, 1);
    return [row, ...next];
  }, [
    getApproversAsStaffList,
    getDefaultApprover,
    selectedApprover,
    acknowledgementTemplateData?.data?.policyAcknowledgementApprovers,
  ]);

  const folder = folderData?.data ?? folderData;
  const displayAssignees = useMemo(() => {
    const uniqueAssignees = new Map();
    return assignees.reduce((allAssignees, cur) => {
      const userId = cur.userId ?? cur.user?.id;
      if (userId == null || uniqueAssignees.has(userId)) {
        return allAssignees;
      }
      uniqueAssignees.set(userId, cur);
      allAssignees.push({
        ...cur,
        user: {
          firstName: cur.firstName ?? cur.user?.firstName ?? "",
          lastName: cur.lastName ?? cur.user?.lastName ?? "",
        },
      });
      return allAssignees;
    }, []);
  }, [assignees]);
  const displayManagerUsers = useMemo(() => {
    const allManagerUsers = [];
    const uniqueManagerUsers = new Map();
    selectedManagerUsers.forEach((cur) => {
      if (uniqueManagerUsers.has(cur.userId)) {
        return;
      }
      uniqueManagerUsers.set(cur.userId, cur);
      allManagerUsers.push({
        ...cur,
        user: {
          firstName: cur.firstName || cur.user?.firstName,
          lastName: cur.lastName || cur.user?.lastName,
        },
      });
    });
    return allManagerUsers;
  }, [selectedManagerUsers]);

  /** In edit mode use policy detail's embedded policyFolder for category/offices; no separate folder API call. */
  const folderForPrefill = policy?.policyFolder ?? folder;

  useEffect(() => {
    if (!folderIdDecoded || !folder || formPrefilled || isEditMode) return;
    setTitle(folder?.title ?? "");
    setDescription(folder?.description ?? "");
    setIsAllOffice(!!folder?.isAllOffice);
    if (folder?.policyCategory) {
      const cat = folder.policyCategory;
      setSelectedCategory({ id: cat.id, title: cat.title ?? "" });
    } else if (folder?.policyCategoryId && Array.isArray(categoryList)) {
      const category = categoryList.find(
        (c) => (c.id ?? c.Id) === folder.policyCategoryId,
      );
      if (category) setSelectedCategory(category);
    }
    const officeListForm = mapFolderOfficesToForm(folder?.offices ?? []);
    if (officeListForm.length > 0) {
      setSelectedOfficeList(officeListForm);
      setSelectedOffices(
        officeListForm.reduce((acc, o) => ({ ...acc, [o.id]: true }), {}),
      );
    }
    setFormPrefilled(true);
  }, [folderIdDecoded, folder, categoryList, formPrefilled, isEditMode]);

  /** Keep office selection in sync when folder data loads or updates after a policy version adds offices. */
  useEffect(() => {
    if (!folderIdDecoded || !folder || isEditMode) return;
    const folderHasOffices =
      !!folder?.isAllOffice ||
      (Array.isArray(folder?.offices) && folder.offices.length > 0);
    if (!folderHasOffices) return;

    setIsAllOffice(!!folder.isAllOffice);
    const officeListForm = mapFolderOfficesToForm(folder.offices ?? []);
    setSelectedOfficeList(officeListForm);
    setSelectedOffices(
      officeListForm.reduce((acc, o) => ({ ...acc, [o.id]: true }), {}),
    );
  }, [folderIdDecoded, folder, isEditMode]);

  useEffect(() => {
    if (!isEditMode || !policy || formPrefilledFromPolicy) return;
    setTitle(policy?.policyFolder?.title ?? "");
    setDescription(policy?.description ?? "");
    if (policy?.startDate) {
      const parsedStartDate = parseCalendarDateToLocalDate(policy.startDate);
      if (parsedStartDate) setStartDate(parsedStartDate);
    }
    if (policy?.dueDate) {
      const parsedDueDate = parseCalendarDateToLocalDate(policy.dueDate);
      if (parsedDueDate) setDueDate(parsedDueDate);
    }
    setPriority(policy?.priority ?? PRIORITY_MEDIUM);
    setPolicyDocumentName(policy?.title);
    setNotes(policy?.notes ?? "");
    setVersionNumber(policy?.versionNumber ?? "");
    setNotifyAssignees(!!policy?.notifyAssignees);
    setNotifyAssignor(!!policy?.notifyAssignor);
    setNotifyManagement(!!policy?.isNotifyManagement);
    setManagementOption(
      policy?.isNotifyAllManagementMember ? "all" : "selected",
    );
    setRemindAfterPublish(!!(policy?.remindAfterPublish > 0));
    setRemindAfterPublishDays(policy?.remindAfterPublish || 1);
    setRemindBeforeDueDate(!!(policy?.remindBeforeDueDate > 0));
    setRemindBeforeDueDateDays(policy?.remindBeforeDueDate || 1);
    setCreateTask(!!(policy?.isTaskCreated ?? policy?.isCreateTask));
    const policyCat = policy?.policyCategory;
    if (
      policyCat &&
      typeof policyCat === "object" &&
      !Array.isArray(policyCat)
    ) {
      setSelectedCategory({
        id: policyCat.id ?? policyCat.Id,
        title: policyCat.title ?? policyCat.Title ?? "",
      });
    } else if (Array.isArray(policyCat) && policyCat[0]) {
      const c = policyCat[0];
      setSelectedCategory({
        id: c.id ?? c.Id,
        title: c.title ?? c.Title ?? "",
      });
    } else if (policy?.policyCategoryId && Array.isArray(categoryList)) {
      const cat = categoryList.find(
        (c) => (c.id ?? c.Id) === policy.policyCategoryId,
      );
      if (cat) setSelectedCategory(cat);
    }
    const folderMatchesPolicy =
      folderForPrefill &&
      (policy?.policyFolderId === folderForPrefill?.id ||
        policy?.policyFolderId === folderForPrefill?.policyFolderId);
    const policyHasNoCategory =
      !policy?.policyCategory &&
      !policy?.policyCategoryId &&
      (!Array.isArray(policy?.policyCategory) ||
        policy.policyCategory.length === 0);
    if (
      policyHasNoCategory &&
      folderMatchesPolicy &&
      (folderForPrefill?.policyCategory ||
        (folderForPrefill?.policyCategoryId && Array.isArray(categoryList)))
    ) {
      if (
        folderForPrefill.policyCategory &&
        typeof folderForPrefill.policyCategory === "object"
      ) {
        const cat = folderForPrefill.policyCategory;
        setSelectedCategory({
          id: cat.id ?? cat.Id,
          title: cat.title ?? cat.Title ?? "",
        });
      } else if (Array.isArray(categoryList)) {
        const cat = categoryList.find(
          (c) => (c.id ?? c.Id) === folderForPrefill.policyCategoryId,
        );
        if (cat) setSelectedCategory(cat);
      }
      setIsAllOffice(!!folderForPrefill?.isAllOffice);
    }
    const policyOffices = policy?.offices ?? policy?.officeIds ?? [];
    let officeListForm = Array.isArray(policyOffices)
      ? policyOffices.map((o) => ({
          id: o.officeId ?? o.id,
          name: o.officeName ?? o.name ?? "",
          officeName: o.officeName ?? o.name ?? "",
        }))
      : mapFolderOfficesToForm(policy?.offices ?? []);
    if (
      officeListForm.length === 0 &&
      folderMatchesPolicy &&
      folderForPrefill?.offices
    ) {
      officeListForm = mapFolderOfficesToForm(folderForPrefill.offices);
      if (officeListForm.length > 0) {
        setIsAllOffice(!!folderForPrefill?.isAllOffice);
      }
    }
    if (officeListForm.length > 0) {
      setSelectedOfficeList(officeListForm);
      setSelectedOffices(
        officeListForm.reduce((acc, o) => ({ ...acc, [o.id]: true }), {}),
      );
    }
    const policyDocUrls =
      policy?.policyDocumentUrls ?? policy?.PolicyDocumentUrls;
    if (Array.isArray(policyDocUrls) && policyDocUrls.length > 0) {
      setAttachedFiles(
        policyDocUrls
          .filter((d) => !d.isDeleted && !d.IsDeleted)
          .map((d) => ({
            path: d.filePath ?? d.FilePath ?? d.path ?? "",
            file: null,
            Id: d.id ?? d.Id ?? 0,
            IsDeleted: false,
          })),
      );
    }
    const policyUrls = policy?.urls ?? policy?.Urls;
    if (Array.isArray(policyUrls) && policyUrls.length > 0) {
      setUrls(
        policyUrls.map((u) => ({
          id: u.id ?? u.Id ?? 0,
          filePath: u.filePath ?? u.FilePath ?? u.path ?? "",
          isDeleted: !!(u.isDeleted ?? u.IsDeleted),
        })),
      );
    } else {
      setUrls([{ id: 0, filePath: "", isDeleted: false }]);
    }
    setFormPrefilledFromPolicy(true);
    if (Array.isArray(policy?.assignees) && policy.assignees.length > 0) {
      setAssignees(policy.assignees.map(mapPolicyAssigneeToForm));
    }
    if (policy?.managerUsers) {
      setSelectedManagerUsers([...policy.managerUsers]);
    }
    if (
      policy?.isPolicyAcknowledgementEnabled !== undefined ||
      policy?.IsPolicyAcknowledgementEnabled !== undefined
    ) {
      setAllowPolicyAcknowledgementByStaff(
        !!(
          policy?.isPolicyAcknowledgementEnabled ??
          policy?.IsPolicyAcknowledgementEnabled
        ),
      );
    }
    if (
      policy?.isPolicyAcknowledgementApprovedByEnabled !== undefined ||
      policy?.IsPolicyAcknowledgementApprovedByEnabled !== undefined
    ) {
      setIncludeApprover(
        !!(
          policy?.isPolicyAcknowledgementApprovedByEnabled ??
          policy?.IsPolicyAcknowledgementApprovedByEnabled
        ),
      );
    }
    const policyApproverId = policy?.approverId ?? policy?.ApproverId ?? null;
    const policyApproverName =
      policy?.approverName ?? policy?.ApproverName ?? "";
    if (
      policyApproverId ||
      policyApproverName ||
      (policy?.approver && typeof policy.approver === "object") ||
      (policy?.Approver && typeof policy.Approver === "object")
    ) {
      const approverObject =
        (policy?.approver && typeof policy.approver === "object"
          ? policy.approver
          : null) ??
        (policy?.Approver && typeof policy.Approver === "object"
          ? policy.Approver
          : null);
      const approver =
        approverObject ??
        (() => {
          const approverNameParts = (policyApproverName || "")
            .trim()
            .split(/\s+/)
            .filter(Boolean);
          return {
            id: policyApproverId,
            userId: policyApproverId,
            firstName: approverNameParts[0] || "",
            lastName: approverNameParts.slice(1).join(" ") || "",
          };
        })();
      setSelectedApprover(approver);
    } else if (
      policy &&
      !policy?.approverId &&
      !policy?.ApproverId &&
      !policy?.approverName &&
      !policy?.ApproverName &&
      !policy?.approver &&
      !policy?.Approver
    ) {
      setSelectedApprover(null);
    }
  }, [
    isEditMode,
    policy,
    formPrefilledFromPolicy,
    categoryList,
    folderForPrefill,
  ]);

  useEffect(() => {
    if (!hasAcknowledgementTemplateData || isAcknowledgementTemplateActive) {
      return;
    }
    setAllowPolicyAcknowledgementByStaff(false);
    setIncludeApprover(false);
    setSelectedApprover(null);
    setErrors((prev) => ({ ...prev, approver: undefined }));
  }, [hasAcknowledgementTemplateData, isAcknowledgementTemplateActive]);

  useEffect(() => {
    if (!hasAcknowledgementTemplateData) return;
    const shouldShowIncludeApproverToggle =
      isAcknowledgementTemplateActive &&
      isApproverEnabledInTemplate &&
      allowPolicyAcknowledgementByStaff;
    if (shouldShowIncludeApproverToggle) return;
    setIncludeApprover(false);
    setSelectedApprover(null);
    setErrors((prev) => ({ ...prev, approver: undefined }));
  }, [
    hasAcknowledgementTemplateData,
    isAcknowledgementTemplateActive,
    isApproverEnabledInTemplate,
    allowPolicyAcknowledgementByStaff,
  ]);

  const toggleSection = useCallback((section) => {
    setSectionsOpen((prev) => ({ ...prev, [section]: !prev[section] }));
  }, []);

  const initialOfficeIdsForModal = useMemo(
    () =>
      selectedOfficeList.reduce((acc, o) => {
        const id = o.id ?? o.officeId;
        if (id != null) acc[id] = true;
        return acc;
      }, {}),
    [selectedOfficeList],
  );

  const selectedOfficeIdsArray = useMemo(
    () =>
      selectedOfficeList
        .map((o) => o.id ?? o.officeId)
        .filter((id) => id != null),
    [selectedOfficeList],
  );

  const allOfficeIdsArray = useMemo(() => {
    if (!allOfficesData?.data?.length) return [];
    return allOfficesData.data
      .map((o) => o.id ?? o.officeId)
      .filter((id) => id != null);
  }, [allOfficesData]);

  const staffModalOfficeIds = useMemo(
    () => (isAllOffice ? allOfficeIdsArray : selectedOfficeIdsArray),
    [isAllOffice, allOfficeIdsArray, selectedOfficeIdsArray],
  );

  const handlePopupToggle = useCallback((e, setter) => {
    e.stopPropagation();
    setInvitePopupPosition({ top: 40, right: 40 });
    setter((prev) => !prev);
  }, []);

  const officeDisplayRender = useMemo(() => {
    if (isAllOffice) return t("notes.allFilters");
    if (selectedOfficeList.length === 0)
      return t("officePolicyManagement.noOfficeSelected");
    const names = selectedOfficeList.map((o) => o.name ?? o.officeName ?? "");
    if (names.length <= OFFICE_DISPLAY_COUNT) {
      return (
        <span
          role="button"
          tabIndex={0}
          onKeyDown={(e) =>
            e.key === "Enter" && handlePopupToggle(e, setOfficeListPopupOpen)
          }
        >
          {names.join(", ")}
        </span>
      );
    }
    const firstThree = names.slice(0, OFFICE_DISPLAY_COUNT).join(", ");
    const moreCount = names.length - OFFICE_DISPLAY_COUNT;
    return (
      <span>
        {firstThree},{" "}
        <button
          type="button"
          className="link-btn p-0 border-0 bg-transparent"
          onClick={(e) => handlePopupToggle(e, setOfficeListPopupOpen)}
        >
          +{moreCount} {t("officePolicyManagement.more")}
        </button>
      </span>
    );
  }, [selectedOfficeList, isAllOffice, t, handlePopupToggle]);

  const handleCategoryApply = useCallback((category) => {
    setSelectedCategory(category);
    setCategoryModalOpen(false);
  }, []);

  const handleOfficesApply = useCallback(
    (selectedIds, selectedList, allOfficeSelected) => {
      setSelectedOffices(selectedIds ?? {});
      setSelectedOfficeList(Array.isArray(selectedList) ? selectedList : []);
      setIsAllOffice(!!allOfficeSelected);
      setOfficesModalOpen(false);
    },
    [],
  );

  const maxRemindAfterPublishDays = useMemo(() => {
    if (!dueDate || !startDate) return 5;
    return Math.min(5, daysBetween(startDate, dueDate));
  }, [startDate, dueDate]);

  const maxRemindBeforeDueDateDays = useMemo(() => {
    if (!dueDate) return 5;
    return Math.min(5, Math.max(0, daysFromToday(dueDate)));
  }, [dueDate]);

  const remindAfterPublishOptions = useMemo(
    () =>
      REMIND_DAYS.filter((d) => d <= maxRemindAfterPublishDays).map((d) => ({
        id: String(d),
        name: String(d),
      })),
    [maxRemindAfterPublishDays],
  );

  const remindBeforeDueDateOptions = useMemo(
    () =>
      REMIND_DAYS.filter((d) => d <= maxRemindBeforeDueDateDays).map((d) => ({
        id: String(d),
        name: String(d),
      })),
    [maxRemindBeforeDueDateDays],
  );

  const priorityOptions = useMemo(
    () => [
      { id: PRIORITY_LOW, name: t("officePolicyManagement.low") },
      { id: PRIORITY_MEDIUM, name: t("officePolicyManagement.medium") },
      { id: PRIORITY_HIGH, name: t("officePolicyManagement.high") },
    ],
    [],
  );

  const addUrlField = useCallback(() => {
    setUrls((prev) =>
      prev.filter((urlItem) => !urlItem?.isDeleted && !urlItem?.IsDeleted)
        .length < MAX_URLS
        ? [...prev, { id: 0, filePath: "", isDeleted: false }]
        : prev,
    );
  }, []);

  const removeUrlField = useCallback(
    (index) => {
      setUrls((prev) => {
        const item = prev[index];
        if (isEditMode && item && (item.id ?? 0) > 0) {
          const next = prev.map((u, i) =>
            i === index ? { ...u, isDeleted: true } : u,
          );
          return next;
        }
        const next = prev.filter((_, i) => i !== index);
        return next.length > 0
          ? next
          : [{ id: 0, filePath: "", isDeleted: false }];
      });
    },
    [isEditMode],
  );

  const setUrlAt = useCallback((index, value) => {
    setUrls((prev) => {
      const next = prev.map((u, i) =>
        i === index ? { ...u, filePath: value } : u,
      );
      return next;
    });
  }, []);

  const validUrls = useMemo(
    () =>
      urls
        .filter((u) => !u.isDeleted)
        .map((u) => (u.filePath || "").trim())
        .filter(Boolean),
    [urls],
  );

  const allowedFileTypes = useMemo(
    () => [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "image/png",
      "image/jpeg",
      "image/jpg",
    ],
    [],
  );

  const handleFileChange = useCallback(
    (e) => {
      if (!e?.target?.files) return;
      const files = Array.from(e.target.files);
      if (!files.length) return;
      const next = [...attachedFiles];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!allowedFileTypes.includes(file?.type)) {
          toast.error(t("officePolicyManagement.fileNotSupported"));
          continue;
        }
        if (file?.size > constants.MAX_FILE_SIZE_5_MB) {
          toast.error(t("officePolicyManagement.fileSizeValidation"));
          continue;
        }
        if (next.length >= MAX_FILES) break;
        next.push({
          path: URL.createObjectURL(file),
          file,
          Id: 0,
          IsDeleted: false,
        });
      }
      setAttachedFiles(next.slice(0, MAX_FILES));
      e.target.value = "";
    },
    [attachedFiles, allowedFileTypes, t],
  );

  const handleRemoveFile = useCallback(
    (index) => {
      setAttachedFiles((prev) => {
        const item = prev[index];
        if (isEditMode && item && (item.Id ?? 0) > 0) {
          return prev.map((f, i) =>
            i === index ? { ...f, IsDeleted: true } : f,
          );
        }
        const next = prev.filter((_, i) => i !== index);
        return next;
      });
    },
    [isEditMode],
  );

  const validate = useCallback(
    (isDraft) => {
      if (isDraft) {
        setErrors({});
        return true;
      }

      const next = {};

      const trimmedTitle = (title || "").trim();
      if (!trimmedTitle) {
        next.title = t("officePolicyManagement.titleRequired");
      } else if (trimmedTitle.length > TITLE_MAX_LENGTH) {
        next.title = t("officePolicyManagement.titleMaxLength", {
          max: TITLE_MAX_LENGTH,
        });
      }

      if (!selectedCategory) {
        next.category = t("officePolicyManagement.noCategorySelected");
      }
      if (selectedOfficeList.length === 0 && !isAllOffice) {
        next.offices = t("officePolicyManagement.noOfficeSelected");
      }

      if (!startDate) {
        next.startDate =
          t("officePolicyManagement.startDate") + " is required.";
      }
      if (!dueDate) {
        next.dueDate = t("officePolicyManagement.dueDate") + " is required.";
      }
      if (startDate && dueDate && startDate > dueDate) {
        next.startDate = t(
          "officePolicyManagement.startDateSmallerThanDueDate",
        );
      }
      if (dueDate && startDate && dueDate < startDate) {
        next.dueDate = t("officePolicyManagement.dueDateGreaterThanStartDate");
      }
      const pastDue =
        dueDate && getStartOfDay(dueDate) < getStartOfDay(new Date());
      if (pastDue) {
        next.dueDate =
          t("officePolicyManagement.dueDate") + " cannot be a past date.";
      }

      const docName = (policyDocumentName || "").trim();
      if (!docName) {
        next.policyDocumentName = t(
          "officePolicyManagement.documentNameRequired",
        );
      } else if (docName.length > DOCUMENT_NAME_MAX) {
        next.policyDocumentName = t(
          "officePolicyManagement.documentNameMaxLength",
        );
      }

      const notesTrimmed = (notes || "").trim();
      if (!notesTrimmed) {
        next.notes = t("officePolicyManagement.notesRequired");
      } else if (notesTrimmed.length > NOTES_MAX) {
        next.notes = t("officePolicyManagement.notesMaxLength");
      }

      const versionTrimmed = (versionNumber || "").trim();
      if (versionTrimmed.length > VERSION_MAX) {
        next.versionNumber = t("officePolicyManagement.versionNumberMaxLength");
      }

      if (!assignees || assignees.length === 0) {
        next.assignees = t("officePolicyManagement.selectAtLeastOneAssignee");
      }

      if (
        notifyManagement &&
        managementOption === "selected" &&
        (!selectedManagerUsers || selectedManagerUsers.length === 0)
      ) {
        next.managementMembers = t("form.errors.atleastOneUser");
      }

      if (
        isAcknowledgementTemplateActive &&
        allowPolicyAcknowledgementByStaff &&
        includeApprover &&
        !selectedApprover
      ) {
        next.approver = `${t("officePolicyManagement.selectAprrover")} is required.`;
      }

      validUrls.forEach((u, i) => {
        if (!isValidUrl(u)) {
          next[`url_${i}`] = t("officePolicyManagement.urlInvalidFormat");
        }
      });

      setErrors(next);
      return Object.keys(next).length === 0;
    },
    [
      title,
      selectedCategory,
      selectedOfficeList,
      isAllOffice,
      startDate,
      dueDate,
      policyDocumentName,
      notes,
      versionNumber,
      validUrls,
      assignees,
      notifyManagement,
      managementOption,
      selectedManagerUsers,
      isAcknowledgementTemplateActive,
      allowPolicyAcknowledgementByStaff,
      includeApprover,
      selectedApprover,
      t,
    ],
  );

  const buildPayload = useCallback(
    (isDraft, policyDocumentUrlsPayload) => {
      const assigneeStaffMemberIds = [];

      if (isEditMode) {
        const alreadyAddedAssignees = new Map();

        assignees.forEach((curr) => {
          const officeId = curr.offices?.[0]?.officeId ?? curr.officeId ?? null;
          const userId = curr.userId ?? curr.user?.id;
          if (officeId == null || userId == null) return;

          const key = `${userId}-${officeId}`;
          if (alreadyAddedAssignees.has(key)) return;
          alreadyAddedAssignees.set(key, true);

          assigneeStaffMemberIds.push({
            Id: curr.id ?? 0,
            UserId: userId,
            OfficeId: officeId,
            IsOwner: !!curr.isOwner,
            IsDeleted: false,
          });
        });

        policy?.assignees?.forEach((existing) => {
          const officeId = existing.officeId ?? null;
          const userId = existing.user?.id ?? existing.userId;
          if (officeId == null || userId == null) return;

          const key = `${userId}-${officeId}`;
          if (alreadyAddedAssignees.has(key)) return;
          alreadyAddedAssignees.set(key, true);

          assigneeStaffMemberIds.push({
            Id: existing.id ?? 0,
            UserId: userId,
            OfficeId: officeId,
            IsOwner: Number(userId) === Number(existing.ownerId),
            IsDeleted: true,
          });
        });
      } else {
        assignees.forEach((curr) => {
          if (curr.offices?.length) {
            const values = curr.offices[0];
            assigneeStaffMemberIds.push({
              UserId: curr.userId,
              OfficeId: values.officeId,
              IsOwner: curr.isOwner,
            });
          }
        });
      }

      const urlsPayload = isEditMode
        ? urls.map((u) => ({
            Id: u.id ?? 0,
            FilePath: (u.filePath || "").trim(),
            IsDeleted: !!u.isDeleted,
          }))
        : validUrls.map((url) => ({
            Id: 0,
            FilePath: url,
            IsDeleted: false,
          }));

      const managerUsersPayload = [];
      if (isEditMode) {
        const alreadyAddedManagerUsers = new Map();
        selectedManagerUsers.forEach(({ userId, id }) => {
          if (alreadyAddedManagerUsers.has(userId)) {
            return;
          }
          alreadyAddedManagerUsers.set(userId, true);
          managerUsersPayload.push({
            Id: id ?? 0,
            UserId: userId,
            IsDeleted: false,
          });
        });
        policy?.managerUsers?.forEach(({ userId, id }) => {
          if (alreadyAddedManagerUsers.has(userId)) {
            return;
          }
          alreadyAddedManagerUsers.set(userId, true);
          managerUsersPayload.push({
            Id: id,
            UserId: userId,
            IsDeleted: true,
          });
        });
      } else {
        selectedManagerUsers.forEach(({ userId }) =>
          managerUsersPayload.push({
            Id: 0,
            UserId: userId,
            IsDeleted: false,
          }),
        );
      }

      const startDateIso = startDate
        ? new Date(
            Date.UTC(
              startDate.getFullYear(),
              startDate.getMonth(),
              startDate.getDate(),
              0,
              0,
              0,
              0,
            ),
          ).toISOString()
        : null;
      const dueDateIso = dueDate
        ? new Date(
            Date.UTC(
              dueDate.getFullYear(),
              dueDate.getMonth(),
              dueDate.getDate(),
              0,
              0,
              0,
              0,
            ),
          ).toISOString()
        : null;

      const selectedOfficeIds = isAllOffice
        ? allOfficeIdsArray
        : selectedOfficeIdsArray;
      const isCategoryAddedfromFolder = !!folderForPrefill?.policyCategoryId;
      const hasOfficesAddedfromFolder = !!(
        folderForPrefill?.offices?.length > 0 || folderForPrefill?.isAllOffice
      );

      const basePayload = {
        title: (policyDocumentName || "").trim(),
        description: (description || "").trim(),
        startDate: startDateIso,
        dueDate: dueDateIso,
        priority: Number(priority) || PRIORITY_MEDIUM,
        assignees: assigneeStaffMemberIds,
        notifyAssignees: !!notifyAssignees,
        notifyAssignor: !!notifyAssignor,
        PolicyDocumentUrls: policyDocumentUrlsPayload || [],
        PolicyFolderId: folderIdDecoded,
        selectedCategoryId: selectedCategory?.id ?? null,
        selectedOfficeIds,
        IsCategoryAddedfromFolder: isCategoryAddedfromFolder,
        hasOfficesAddedfromFolder,
        Notes: (notes || "").trim(),
        VersionNumber: (versionNumber || "").trim(),
        IsNotifyManagement: !!notifyManagement,
        IsNotifyAllManagementMember: managementOption === "all",
        RemindAfterPublish: remindAfterPublish ? remindAfterPublishDays : 0,
        RemindBeforeDueDate: remindBeforeDueDate ? remindBeforeDueDateDays : 0,
        Urls: urlsPayload,
        ManagerUsers: managerUsersPayload,
        IsDraft: !!isDraft,
        IsCreateTask: !!createTask,
      };
      if (isEditMode && policyIdDecoded) {
        basePayload.policyId = policyIdDecoded;
      } else {
        basePayload.filePath =
          attachedFiles.length && attachedFiles[0]?.file
            ? attachedFiles[0].file.name
            : "";
        basePayload.accountOwnerId = accountOwnerId ?? null;
      }
      if (!isAcknowledgementTemplateActive) {
        basePayload.IsPolicyAcknowledgementEnabled = false;
        basePayload.IsPolicyAcknowledgementApprovedByEnabled = false;
        basePayload.ApproverId = null;
        basePayload.ApproverName = "";
      } else {
        basePayload.IsPolicyAcknowledgementEnabled =
          !!allowPolicyAcknowledgementByStaff;
        basePayload.IsPolicyAcknowledgementApprovedByEnabled =
          !!includeApprover;
        const approverId =
          includeApprover && selectedApprover
            ? (selectedApprover.id ?? selectedApprover.userId ?? null)
            : null;
        const approverName =
          includeApprover && selectedApprover
            ? `${selectedApprover.firstName ?? selectedApprover.FirstName ?? ""} ${selectedApprover.lastName ?? selectedApprover.LastName ?? ""}`.trim()
            : "";
        basePayload.ApproverId = approverId;
        basePayload.ApproverName = approverName || "";
      }
      return basePayload;
    },
    [
      isAllOffice,
      selectedOfficeList,
      selectedOfficeIdsArray,
      allOfficeIdsArray,
      selectedCategory,
      folderForPrefill,
      assignees,
      selectedManagerUsers,
      urls,
      validUrls,
      policyDocumentName,
      description,
      startDate,
      dueDate,
      priority,
      notifyAssignees,
      notifyAssignor,
      attachedFiles,
      isEditMode,
      accountOwnerId,
      folderIdDecoded,
      notes,
      versionNumber,
      notifyManagement,
      managementOption,
      remindAfterPublish,
      remindAfterPublishDays,
      remindBeforeDueDate,
      remindBeforeDueDateDays,
      createTask,
      policyIdDecoded,
      policy,
      isAcknowledgementTemplateActive,
      allowPolicyAcknowledgementByStaff,
      includeApprover,
      selectedApprover,
    ],
  );

  const buildPolicyDocumentUrlsPayload = useCallback(async () => {
    const result = [];
    for (const f of attachedFiles || []) {
      if (f.IsDeleted) {
        result.push({
          Id: f.Id || 0,
          FilePath: typeof f.path === "string" ? f.path : "",
          IsDeleted: true,
        });
        continue;
      }
      if (f.file && f.path) {
        const [err, blobData] = await uploadImage(
          f.file,
          POLICY_DOCUMENTS_CONTAINER,
          () => {},
          () => {},
          `policyFolder/${folderIdDecoded}`,
        );
        if (err) throw new Error(err?.message || "File upload failed");
        result.push({
          Id: f.Id || 0,
          FilePath: `${POLICY_DOCUMENTS_CONTAINER}/${blobData.blobName}`,
          IsDeleted: false,
        });
      } else if (f.path && !f.file) {
        const path = typeof f.path === "string" ? f.path : "";
        result.push({
          Id: f.Id || 0,
          FilePath: path,
          IsDeleted: false,
        });
      }
    }
    return result;
  }, [attachedFiles]);

  useEffect(() => {
    if (selectedManagerUsers?.length > 0) {
      setErrors((prev) => ({ ...prev, managementMembers: undefined }));
    }
  }, [selectedManagerUsers]);

  useEffect(() => {
    if (assignees?.length > 0) {
      setErrors((prev) => ({ ...prev, assignees: undefined }));
    }
  }, [assignees]);

  const handleSubmit = useCallback(
    async (isDraft) => {
      if (!validate(isDraft)) {
        scrollToError();
        return;
      }
      setSaving(true);
      setErrors({});
      try {
        const policyDocumentUrlsPayload =
          await buildPolicyDocumentUrlsPayload();
        const payload = buildPayload(isDraft, policyDocumentUrlsPayload);
        if (isEditMode) {
          await updatePolicy(payload);
          toast.success(
            t("officePolicyManagement.policyFolderUpdated") ||
              "Policy updated successfully.",
          );
        } else {
          await createPolicy(payload);
          queryClient.invalidateQueries(["/PolicyFolder", folderIdDecoded]);
          toast.success(
            isDraft
              ? t("officePolicyManagement.saveAsDraft") + " saved."
              : "Policy published successfully.",
          );
        }
        const listingPath =
          constants.routes.officePolicyManagements.policyDocumentListing.replace(
            ":officePolicyFolderId",
            officePolicyFolderId || "",
          );
        history.replace({
          pathname: listingPath,
          search: selectedOwnerIdString ? `?${selectedOwnerIdString}` : "",
        });
      } catch (err) {
        const message = err?.message || "";
        const isVersionExists =
          message.toLowerCase().includes("version") &&
          message.toLowerCase().includes("exists");
        if (isVersionExists) {
          setErrors({
            versionNumber: t(
              "officePolicyManagement.policyVersionAlreadyExists",
            ),
          });
        } else {
          toast.error(message);
        }
      } finally {
        setSaving(false);
      }
    },
    [
      validate,
      buildPolicyDocumentUrlsPayload,
      buildPayload,
      history,
      officePolicyFolderId,
      selectedOwnerIdString,
      t,
      isEditMode,
      queryClient,
      folderIdDecoded,
    ],
  );

  const onBack = useCallback(() => {
    if (window.history.length > 1) {
      return history.goBack();
    }

    history.push(
      constants.routes.officePolicyManagements.policyDocumentListing.replace(
        ":officePolicyFolderId",
        officePolicyFolderId || "",
      ),
    );
  }, [history, officePolicyFolderId]);

  const assigneesForModal = useMemo(
    () =>
      assignees.map((a) => ({
        ...a,
        id: a.id ?? 0,
        userId: a?.userId ?? a?.user?.id,
        firstName: a.firstName ?? a.user?.firstName ?? "",
        lastName: a.lastName ?? a.user?.lastName ?? "",
        offices:
          a.offices?.length > 0
            ? a.offices
            : a.officeId != null
              ? [{ officeId: a.officeId }]
              : [],
        IsDeleted: false,
        isDeleted: false,
      })),
    [assignees],
  );

  const assigneesDisplayRender = useMemo(() => {
    if (displayAssignees?.length === 0)
      return t("officePolicyManagement.noMembers");
    const names = displayAssignees.map(
      (a) => a.user.firstName + " " + a.user.lastName,
    );
    if (names.length <= OFFICE_DISPLAY_COUNT) {
      return (
        <span
          className={officePolicyStyles["member-names-link"]}
          onClick={(e) => handlePopupToggle(e, setAssigneesPopupOpen)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) =>
            e.key === "Enter" && handlePopupToggle(e, setAssigneesPopupOpen)
          }
        >
          {names.join(", ")}
        </span>
      );
    }
    const firstThree = names.slice(0, OFFICE_DISPLAY_COUNT).join(", ");
    const moreCount = names.length - OFFICE_DISPLAY_COUNT;
    return (
      <span>
        <span
          className={officePolicyStyles["member-names-link"]}
          role="button"
          tabIndex={0}
          onClick={(e) => handlePopupToggle(e, setAssigneesPopupOpen)}
          onKeyDown={(e) =>
            e.key === "Enter" && handlePopupToggle(e, setAssigneesPopupOpen)
          }
        >
          {firstThree}
        </span>
        ,{" "}
        <button
          type="button"
          className="link-btn p-0 border-0 bg-transparent"
          onClick={(e) => handlePopupToggle(e, setAssigneesPopupOpen)}
        >
          +{moreCount} {t("officePolicyManagement.more")}
        </button>
      </span>
    );
  }, [displayAssignees, t, handlePopupToggle]);
  const managerMembersDisplayRender = useMemo(() => {
    if (displayManagerUsers.length === 0)
      return t("officePolicyManagement.noMembers");
    const names = displayManagerUsers.map(
      (m) => m.user.firstName + " " + m.user.lastName,
    );
    if (names.length <= OFFICE_DISPLAY_COUNT) {
      return (
        <span
          className={officePolicyStyles["member-names-link"]}
          onClick={(e) => handlePopupToggle(e, setManagerMembersPopupOpen)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) =>
            e.key === "Enter" &&
            handlePopupToggle(e, setManagerMembersPopupOpen)
          }
        >
          {names.join(", ")}
        </span>
      );
    }
    const firstThree = names.slice(0, OFFICE_DISPLAY_COUNT).join(", ");
    const moreCount = names.length - OFFICE_DISPLAY_COUNT;
    return (
      <span>
        <span
          className={officePolicyStyles["member-names-link"]}
          role="button"
          tabIndex={0}
          onClick={(e) => handlePopupToggle(e, setManagerMembersPopupOpen)}
          onKeyDown={(e) =>
            e.key === "Enter" &&
            handlePopupToggle(e, setManagerMembersPopupOpen)
          }
        >
          {firstThree}
        </span>
        ,{" "}
        <button
          type="button"
          className="link-btn p-0 border-0 bg-transparent"
          onClick={(e) => handlePopupToggle(e, setManagerMembersPopupOpen)}
        >
          +{moreCount} {t("officePolicyManagement.more")}
        </button>
      </span>
    );
  }, [displayManagerUsers, t, handlePopupToggle]);

  return (
    <div className={styles.pageContainer}>
      {(saving || isLoading || isFetching) && <Loader />}
      <div className={styles.backBtnContainer}>
        <button type="button" className="back-btn" onClick={onBack}>
          <div className="back-btn-text">
            <span className="ico">
              <img
                src={require("assets/images/arrow-back-icon.svg").default}
                alt="arrow"
              />
            </span>
            {t("back")}
          </div>
        </button>
        <h2 className="page-title mb-0">
          {isEditMode
            ? t("officePolicyManagement.editPolicyDocument")
            : t("officePolicyManagement.addNewPolicyDocument")}
        </h2>
      </div>
      <main
        className={styles.card}
        style={{ border: "none", boxShadow: "none" }}
      >
        <div className={officePolicyStyles["add-new-policy-box"]}>
          <SectionCard
            title="Policy Details"
            isOpen={sectionsOpen.policyDetails}
            onToggle={() => toggleSection("policyDetails")}
          >
            <div className={`disabled ${officePolicyStyles["form-group"]}`}>
              <label className={officePolicyStyles["field-label"]}>
                {t("officePolicyManagement.policyName")} / {t("notes.title")}
              </label>
              <input
                type="text"
                className={officePolicyStyles["clean-input"]}
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  setErrors((prev) => ({ ...prev, title: undefined }));
                }}
                maxLength={TITLE_MAX_LENGTH}
                disabled
              />
              {errors.title && (
                <div
                  className={officePolicyStyles["field-error"] + " error-msg"}
                >
                  {errors.title}
                </div>
              )}
            </div>

            <div className={officePolicyStyles["form-group"]}>
              <label className={officePolicyStyles["field-label"]}>
                {t("officePolicyManagement.description")}
              </label>
              <textarea
                className={officePolicyStyles["clean-textarea"]}
                rows="2"
                value={description}
                maxLength={DESCRIPTION_MAX}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div
              className={`${officePolicyStyles["form-group"]} ${(isEditMode && !!selectedCategory) || (!isEditMode && !!folder?.policyCategoryId) ? "disabled" : ""}`}
            >
              <div className="d-flex justify-content-between align-items-center">
                <label className={officePolicyStyles["field-label"]}>
                  {t("officePolicyManagement.category")}
                </label>
                <span
                  className={officePolicyStyles["action-link"]}
                  onClick={() =>
                    !(
                      (isEditMode && selectedCategory) ||
                      (!isEditMode && folder?.policyCategoryId)
                    ) && setCategoryModalOpen(true)
                  }
                  role="button"
                  tabIndex={
                    (isEditMode && !!selectedCategory) ||
                    (!isEditMode && !!folder?.policyCategoryId)
                      ? -1
                      : 0
                  }
                  aria-disabled={
                    (isEditMode && !!selectedCategory) ||
                    (!isEditMode && !!folder?.policyCategoryId)
                  }
                >
                  {t("officePolicyManagement.select")}
                </span>
              </div>
              <div className={officePolicyStyles["field-value"]}>
                {selectedCategory?.title ??
                  t("officePolicyManagement.noCategorySelected")}
              </div>
              {errors.category && (
                <div
                  className={officePolicyStyles["field-error"] + " error-msg"}
                >
                  {errors.category}
                </div>
              )}
            </div>

            <div
              className={`${officePolicyStyles["form-group"]} position-relative ${(isEditMode && (selectedOfficeList.length > 0 || isAllOffice)) || (!isEditMode && (folder?.offices?.length > 0 || folder?.isAllOffice)) ? "disabled" : ""}`}
            >
              <div className="d-flex justify-content-between align-items-center">
                <label className={officePolicyStyles["field-label"]}>
                  {t("officePolicyManagement.office")}
                </label>
                <span
                  className={officePolicyStyles["action-link"]}
                  onClick={() => {
                    const officeDisabled =
                      (isEditMode &&
                        (selectedOfficeList.length > 0 || isAllOffice)) ||
                      (!isEditMode &&
                        (folder?.offices?.length > 0 || folder?.isAllOffice));
                    if (!officeDisabled) {
                      setSelectedOffices(initialOfficeIdsForModal);
                      setOfficesModalOpen(true);
                    }
                  }}
                  role="button"
                  tabIndex={
                    (isEditMode &&
                      (selectedOfficeList.length > 0 || isAllOffice)) ||
                    (!isEditMode &&
                      (folder?.offices?.length > 0 || folder?.isAllOffice))
                      ? -1
                      : 0
                  }
                  aria-disabled={
                    (isEditMode &&
                      (selectedOfficeList.length > 0 || isAllOffice)) ||
                    (!isEditMode &&
                      (folder?.offices?.length > 0 || folder?.isAllOffice))
                  }
                >
                  {t("officePolicyManagement.select")}
                </span>
              </div>
              <div className={officePolicyStyles["field-value"]}>
                {officeDisplayRender}
              </div>
              {officeListPopupOpen && selectedOfficeList.length > 0 && (
                <InviteesPopup
                  position={invitePopupPosition}
                  handleInvitePopupToggle={(e) =>
                    handlePopupToggle(e, setOfficeListPopupOpen)
                  }
                  list={selectedOfficeList}
                  onlyOfffice
                  isForEvent
                />
              )}
              {errors.offices && (
                <div
                  className={officePolicyStyles["field-error"] + " error-msg"}
                >
                  {errors.offices}
                </div>
              )}
            </div>

            <div className={officePolicyStyles["form-group-last"] + " c-field"}>
              <label className={officePolicyStyles["field-label"]}>
                {t("officePolicyManagement.startDate")}
              </label>
              <div className="d-flex justify-content-between align-items-center inputdate">
                <ReactDatePickerCommon
                  dateFormat="dd-MM-yyyy"
                  className={`c-form-control border-0 p-0 shadow-none ${officePolicyStyles["date-picker-input"]}`}
                  onChange={(date) => setStartDate(date)}
                  selected={startDate}
                />
              </div>
              {errors.startDate && (
                <div
                  className={officePolicyStyles["field-error"] + " error-msg"}
                >
                  {errors.startDate}
                </div>
              )}
            </div>
          </SectionCard>

          <SectionCard
            title="Add Document Details"
            isOpen={sectionsOpen.documentDetails}
            onToggle={() => toggleSection("documentDetails")}
          >
            <div className={officePolicyStyles["form-group"]}>
              <label className={officePolicyStyles["field-label"]}>
                {t("officePolicyManagement.policyDocumentName")}
              </label>
              <input
                type="text"
                className={officePolicyStyles["clean-input"]}
                value={policyDocumentName}
                onChange={(e) => {
                  setPolicyDocumentName(e.target.value);
                  setErrors((prev) => ({
                    ...prev,
                    policyDocumentName: undefined,
                  }));
                }}
                maxLength={DOCUMENT_NAME_MAX}
              />
              {errors.policyDocumentName && (
                <div
                  className={officePolicyStyles["field-error"] + " error-msg"}
                >
                  {errors.policyDocumentName}
                </div>
              )}
            </div>

            <div className={officePolicyStyles["form-group"]}>
              <label className={officePolicyStyles["field-label"]}>
                {t("officePolicyManagement.notes")}
              </label>
              <textarea
                className={officePolicyStyles["clean-textarea"]}
                rows="2"
                value={notes}
                onChange={(e) => {
                  setNotes(e.target.value);
                  setErrors((prev) => ({ ...prev, notes: undefined }));
                }}
                maxLength={NOTES_MAX}
              />
              {errors.notes && (
                <div
                  className={officePolicyStyles["field-error"] + " error-msg"}
                >
                  {errors.notes}
                </div>
              )}
            </div>

            <div className={officePolicyStyles["form-group"]}>
              <label className={officePolicyStyles["field-label"]}>
                {t("officePolicyManagement.versionNumber")}
              </label>
              <input
                type="text"
                className={officePolicyStyles["clean-input"]}
                value={versionNumber}
                onChange={(e) => {
                  setVersionNumber(e.target.value);
                  setErrors((prev) => ({ ...prev, versionNumber: undefined }));
                }}
                maxLength={VERSION_MAX}
              />
              {errors.versionNumber && (
                <div
                  className={officePolicyStyles["field-error"] + " error-msg"}
                >
                  {errors.versionNumber}
                </div>
              )}
            </div>

            <div className={officePolicyStyles["form-group"]}>
              <div className="d-flex justify-content-between align-items-center">
                <label className={officePolicyStyles["field-label"]}>
                  {t("officePolicyManagement.attachFiles")}{" "}
                </label>
                <div className="position-relative">
                  <span
                    className={officePolicyStyles["action-link"]}
                    onClick={() => fileInputRef.current?.click()}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        fileInputRef.current?.click();
                      }
                    }}
                  >
                    Upload File
                  </span>
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.png,.jpg,.jpeg"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              <div className={officePolicyStyles["field-value"]}>
                {attachedFiles.filter((f) => !f.IsDeleted).length === 0
                  ? t("officePolicyManagement.noAttachments")
                  : attachedFiles
                      .filter((f) => !f.IsDeleted)
                      .map((f, i) => {
                        const idx = attachedFiles.indexOf(f);
                        const path = typeof f.path === "string" ? f.path : "";
                        const isApiFile =
                          !f.file &&
                          path &&
                          (path.startsWith("http") ||
                            path.startsWith("policy-documents/"));
                        const fileUrl = path
                          ? path.startsWith("http")
                            ? path
                            : `https://${process.env.REACT_APP_AZURE_STORAGE_ACCOUNT || ""}.blob.core.windows.net/${path}`
                          : "#";
                        const fileNameDisplay =
                          f.file?.name ||
                          (path ? path.split("/").pop() : "Unknown file");
                        return (
                          <div
                            key={idx}
                            className="d-flex justify-content-between align-items-center"
                            style={{ marginBottom: 4 }}
                          >
                            {isApiFile ? (
                              <a
                                href={fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="break_all"
                                style={{ marginRight: 8 }}
                              >
                                {fileNameDisplay}
                              </a>
                            ) : (
                              <span className="break_all">
                                {fileNameDisplay}
                              </span>
                            )}
                            <TrashIcon onClick={() => handleRemoveFile(idx)} />
                          </div>
                        );
                      })}
              </div>
            </div>

            <div className={officePolicyStyles["form-group"]}>
              <label className={officePolicyStyles["field-label"]}>
                {t("officePolicyManagement.addUrls")}
              </label>
              {urls.map((urlItem, index) =>
                urlItem?.isDeleted || urlItem?.IsDeleted ? null : (
                  <div
                    key={index}
                    className="mb-2 d-flex align-items-center"
                    style={urlItem.isDeleted ? { opacity: 0.6 } : undefined}
                  >
                    <input
                      type="text"
                      className={`${officePolicyStyles["clean-input"]} flex-grow-1 mr-2`}
                      value={urlItem.filePath}
                      onChange={(e) => setUrlAt(index, e.target.value)}
                      placeholder="https://"
                      disabled={urlItem.isDeleted}
                    />
                    {filteredUrls.length > 1 && (
                      <TrashIcon onClick={() => removeUrlField(index)} />
                    )}
                  </div>
                ),
              )}
              {filteredUrls.length < MAX_URLS && (
                <span
                  className={officePolicyStyles["action-link"]}
                  onClick={addUrlField}
                  role="button"
                  tabIndex={0}
                >
                  {t("officePolicyManagement.addUrl")}
                </span>
              )}
              {Object.keys(errors).some((k) => k.startsWith("url_")) && (
                <div
                  className={officePolicyStyles["field-error"] + " error-msg"}
                >
                  {t("officePolicyManagement.urlInvalidFormat")}
                </div>
              )}
            </div>

            {isAcknowledgementTemplateActive && (
              <div
                className={
                  lockAcknowledgementApproverFields
                    ? "custom-disabled-field"
                    : ""
                }
              >
                <div className="d-flex align-items-center gap-2 mb-3">
                  <ToggleSwitch
                    label="policy-acknowledgment-toggle"
                    customClass="mt-2 ml-0 mb-2"
                    value={!!allowPolicyAcknowledgementByStaff}
                    onChange={() =>
                      !lockAcknowledgementApproverFields &&
                      setAllowPolicyAcknowledgementByStaff((prev) => !prev)
                    }
                    disabled={lockAcknowledgementApproverFields}
                  />
                  <Text
                    size="14px"
                    weight="600"
                    color="#2F3245"
                    className="mb-0 text-capitalize"
                  >
                    {t("accountPreferences.policyAcknowledgeByStaff.allow")}
                  </Text>
                </div>
                {isApproverEnabledInTemplate &&
                  allowPolicyAcknowledgementByStaff && (
                    <>
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <ToggleSwitch
                          label="approver-toggle"
                          customClass="mt-2 ml-0 mb-2"
                          value={!!includeApprover}
                          onChange={() => {
                            if (!lockAcknowledgementApproverFields) {
                              setIncludeApprover((prev) => !prev);
                              if (includeApprover) {
                                setSelectedApprover(null);
                              }
                              setErrors((prev) => ({
                                ...prev,
                                approver: undefined,
                              }));
                            }
                          }}
                          disabled={lockAcknowledgementApproverFields}
                        />
                        <Text
                          size="14px"
                          weight="600"
                          color="#2F3245"
                          className="mb-0 text-capitalize"
                        >
                          {t(
                            "accountPreferences.policyAcknowledgeByStaff.includeApprover",
                          )}
                        </Text>
                      </div>
                      {includeApprover && (
                        <div
                          className={
                            "c-field " +
                            officePolicyStyles["staff-selection-border"]
                          }
                        >
                          <Text size="13px" weight="400" color="#79869A">
                            {t("officePolicyManagement.selectAprrover")}
                          </Text>
                          <div className="d-flex align-items-center justify-content-between">
                            <Text size="14px" weight="600" color="#102C42">
                              {selectedApprover
                                ? formatApproverDisplayName(selectedApprover)
                                : t("officePolicyManagement.noMemberSelected")}
                            </Text>
                            {!lockAcknowledgementApproverFields && (
                              <span
                                className={officePolicyStyles["action-link"]}
                                onClick={() => setSelectApproverModalOpen(true)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) =>
                                  e.key === "Enter" &&
                                  setSelectApproverModalOpen(true)
                                }
                              >
                                {t("officePolicyManagement.select")}
                              </span>
                            )}
                          </div>
                          {errors.approver && (
                            <span
                              className={
                                officePolicyStyles["field-error"] + " error-msg"
                              }
                            >
                              {errors.approver}
                            </span>
                          )}
                        </div>
                      )}
                    </>
                  )}
              </div>
            )}
          </SectionCard>

          <SectionCard
            title="Add Assignees"
            isOpen={sectionsOpen.assignees}
            onToggle={() => toggleSection("assignees")}
          >
            <div className={officePolicyStyles["form-group"] + " c-field"}>
              <label className={officePolicyStyles["field-label"]}>
                {t("officePolicyManagement.dueDate")}
              </label>
              <div className="d-flex justify-content-between align-items-center inputdate">
                <ReactDatePickerCommon
                  dateFormat="dd-MM-yyyy"
                  minDate={new Date()}
                  className={`c-form-control border-0 p-0 shadow-none ${officePolicyStyles["date-picker-input"]}`}
                  onChange={(date) => {
                    setDueDate(date);
                    setRemindAfterPublishDays(1);
                    setRemindBeforeDueDateDays(1);
                  }}
                  selected={dueDate}
                />
              </div>
              {errors.dueDate && (
                <div
                  className={officePolicyStyles["field-error"] + " error-msg"}
                >
                  {errors.dueDate}
                </div>
              )}
            </div>

            <div className={officePolicyStyles["form-group"]}>
              <label className={officePolicyStyles["field-label"]}>
                {t("officePolicyManagement.priority")}
              </label>
              <div className={officePolicyStyles["custom-select-wrapper"]}>
                <CustomDropdown
                  options={priorityOptions}
                  selectedOption={priority}
                  selectOption={(id) => setPriority(id)}
                  defaultValue={priority}
                />
              </div>
            </div>

            <div
              className={`${officePolicyStyles["form-group"]} position-relative`}
            >
              <div className="d-flex justify-content-between align-items-center">
                <label className={officePolicyStyles["field-label"]}>
                  {t("officePolicyManagement.assignees")}
                </label>
                <span
                  className={officePolicyStyles["action-link"]}
                  onClick={() => {
                    if (staffModalOfficeIds.length === 0) {
                      toast.error(t("officePolicyManagement.noOfficeSelected"));
                      return;
                    }
                    setErrors((prev) => ({ ...prev, assignees: undefined }));
                    setStaffModalOpen(true);
                  }}
                  role="button"
                  tabIndex={0}
                >
                  {t("officePolicyManagement.assignStaffMembers")}
                </span>
              </div>
              <div className={officePolicyStyles["field-value"]}>
                {assigneesDisplayRender}
              </div>
              {assigneesPopupOpen && displayAssignees?.length > 0 && (
                <InviteesPopup
                  position={invitePopupPosition}
                  handleInvitePopupToggle={(e) =>
                    handlePopupToggle(e, setAssigneesPopupOpen)
                  }
                  list={displayAssignees}
                />
              )}
              {errors.assignees && (
                <div
                  className={officePolicyStyles["field-error"] + " error-msg"}
                >
                  {errors.assignees}
                </div>
              )}
            </div>

            <div className={officePolicyStyles["checkbox-group-wrapper"]}>
              <div className="mb-3">
                <CustomCheckbox
                  label={
                    <>
                      {t("officePolicyManagement.notifyAssignees")}{" "}
                      <img
                        className="ml-2 cursor-pointer"
                        src={tooltipImg}
                        alt="info"
                        id="TooltipNotifyAssignees"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleTooltip("notifyAssignees");
                        }}
                      />
                    </>
                  }
                  checked={notifyAssignees}
                  onChange={() => setNotifyAssignees(!notifyAssignees)}
                />
                <Tooltip
                  className="new-item-card-catalogue-tooltip"
                  isOpen={tooltipOpen.notifyAssignees}
                  placement="top"
                  target="TooltipNotifyAssignees"
                  toggle={() => toggleTooltip("notifyAssignees")}
                >
                  {t("officePolicyManagement.notifyAssigneesTooltip")}
                </Tooltip>
              </div>

              <div className="mb-3">
                <CustomCheckbox
                  label={
                    <>
                      {t("officePolicyManagement.notifyManagement")}{" "}
                      <img
                        className="ml-2 cursor-pointer"
                        src={tooltipImg}
                        alt="info"
                        id="TooltipNotifyManagement"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleTooltip("notifyManagement");
                        }}
                      />
                    </>
                  }
                  checked={notifyManagement}
                  onChange={() => setNotifyManagement(!notifyManagement)}
                />
                <Tooltip
                  className="new-item-card-catalogue-tooltip"
                  isOpen={tooltipOpen.notifyManagement}
                  placement="top"
                  target="TooltipNotifyManagement"
                  toggle={() => toggleTooltip("notifyManagement")}
                >
                  {t("officePolicyManagement.notifyManagementTooltip")}
                </Tooltip>

                {notifyManagement && (
                  <div className={officePolicyStyles["nested-radio-container"]}>
                    <label
                      className={` ${officePolicyStyles["radio-label"]} ${officePolicyStyles["border-bottom"]} `}
                    >
                      <input
                        type="radio"
                        name="managementOption"
                        checked={managementOption === "all"}
                        onChange={() => {
                          setManagementOption("all");
                          setErrors((prev) => ({
                            ...prev,
                            managementMembers: undefined,
                          }));
                        }}
                        className={officePolicyStyles["radio-input"]}
                      />
                      {t("officePolicyManagement.allMembers")}
                    </label>

                    <div className="d-flex justify-content-between align-items-start mb-0">
                      <label
                        className={` ${officePolicyStyles["radio-label"]} mb-0 pb-0`}
                      >
                        <input
                          type="radio"
                          name="managementOption"
                          checked={managementOption === "selected"}
                          onChange={() => setManagementOption("selected")}
                          className={officePolicyStyles["radio-input"]}
                        />
                        {t("officePolicyManagement.selectedMembers")}
                      </label>
                      {managementOption === "selected" && (
                        <span
                          className={officePolicyStyles["action-link"]}
                          onClick={() => {
                            if (staffModalOfficeIds.length === 0) {
                              toast.error(
                                t("officePolicyManagement.noOfficeSelected"),
                              );
                              return;
                            }
                            setManagerModalOpen(true);
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          {t("officePolicyManagement.select")}
                        </span>
                      )}
                    </div>

                    {managementOption === "selected" && (
                      <div
                        className={`pl-4 pb-1 position-relative  ${officePolicyStyles["border-bottom"]}`}
                      >
                        <div
                          className={officePolicyStyles["field-value-small"]}
                        >
                          {managerMembersDisplayRender}
                        </div>
                        {managerMembersPopupOpen &&
                          displayManagerUsers.length > 0 && (
                            <InviteesPopup
                              position={invitePopupPosition}
                              handleInvitePopupToggle={(e) =>
                                handlePopupToggle(e, setManagerMembersPopupOpen)
                              }
                              list={displayManagerUsers}
                            />
                          )}
                        {errors.managementMembers && (
                          <div
                            className={
                              officePolicyStyles["field-error"] + " error-msg"
                            }
                          >
                            {errors.managementMembers}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <CustomCheckbox
                  label={
                    <>
                      {t("officePolicyManagement.remindAfterPublish")}{" "}
                      <img
                        className="ml-2 cursor-pointer"
                        src={tooltipImg}
                        alt="info"
                        id="TooltipRemindAfterPublish"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleTooltip("remindAfterPublish");
                        }}
                      />
                    </>
                  }
                  checked={remindAfterPublish}
                  onChange={() => setRemindAfterPublish(!remindAfterPublish)}
                />
                <Tooltip
                  className="new-item-card-catalogue-tooltip"
                  isOpen={tooltipOpen.remindAfterPublish}
                  placement="top"
                  target="TooltipRemindAfterPublish"
                  toggle={() => toggleTooltip("remindAfterPublish")}
                >
                  {t("officePolicyManagement.remindAfterPublishTooltip")}
                </Tooltip>
                {remindAfterPublish && remindAfterPublishOptions.length > 0 && (
                  <div
                    className={officePolicyStyles["nested-dropdown-container"]}
                  >
                    <label className={officePolicyStyles["field-label"]}>
                      {t("officePolicyManagement.noOfDays")}
                    </label>
                    <div className={officePolicyStyles["mini-dropdown"]}>
                      <CustomDropdown
                        options={remindAfterPublishOptions}
                        selectedOption={String(remindAfterPublishDays)}
                        defaultValue={String(remindAfterPublishDays)}
                        selectOption={(id) =>
                          setRemindAfterPublishDays(Number(id) || 1)
                        }
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="mb-3">
                <CustomCheckbox
                  label={
                    <>
                      {t("officePolicyManagement.remindBeforeDueDate")}{" "}
                      <img
                        className="ml-2 cursor-pointer"
                        src={tooltipImg}
                        alt="info"
                        id="TooltipRemindBeforeDueDate"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleTooltip("remindBeforeDueDate");
                        }}
                      />
                    </>
                  }
                  checked={remindBeforeDueDate}
                  onChange={() => setRemindBeforeDueDate(!remindBeforeDueDate)}
                />
                <Tooltip
                  className="new-item-card-catalogue-tooltip"
                  isOpen={tooltipOpen.remindBeforeDueDate}
                  placement="top"
                  target="TooltipRemindBeforeDueDate"
                  toggle={() => toggleTooltip("remindBeforeDueDate")}
                >
                  {t("officePolicyManagement.remindBeforeDueDateTooltip")}
                </Tooltip>
                {remindBeforeDueDate &&
                  remindBeforeDueDateOptions.length > 0 && (
                    <div
                      className={
                        officePolicyStyles["nested-dropdown-container"]
                      }
                    >
                      <label className={officePolicyStyles["field-label"]}>
                        {t("officePolicyManagement.noOfDays")}
                      </label>
                      <div className={officePolicyStyles["mini-dropdown"]}>
                        <CustomDropdown
                          options={remindBeforeDueDateOptions}
                          selectedOption={String(remindBeforeDueDateDays)}
                          defaultValue={String(remindBeforeDueDateDays)}
                          selectOption={(id) =>
                            setRemindBeforeDueDateDays(Number(id) || 1)
                          }
                        />
                      </div>
                    </div>
                  )}
              </div>

              <div className="mb-0">
                <CustomCheckbox
                  label={
                    <>
                      {t("officePolicyManagement.createTaskForAssignees")}{" "}
                      <img
                        className="ml-2 cursor-pointer"
                        src={tooltipImg}
                        alt="info"
                        id="TooltipCreateTask"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleTooltip("createTask");
                        }}
                      />
                    </>
                  }
                  checked={createTask}
                  onChange={() => setCreateTask(!createTask)}
                  disabled={!!policy?.isTaskCreated}
                />
                <Tooltip
                  className="new-item-card-catalogue-tooltip"
                  isOpen={tooltipOpen.createTask}
                  placement="top"
                  target="TooltipCreateTask"
                  toggle={() => toggleTooltip("createTask")}
                >
                  {t("officePolicyManagement.createTaskForAssigneesTooltip")}
                </Tooltip>
              </div>
            </div>
          </SectionCard>

          <div className={officePolicyStyles["form-footer"]}>
            <button
              type="button"
              className={officePolicyStyles["btn-save"]}
              onClick={() => handleSubmit(false)}
              disabled={saving}
            >
              {t("save")}
            </button>
            <button
              type="button"
              className={officePolicyStyles["btn-cancel"]}
              onClick={onBack}
            >
              {t("cancel")}
            </button>
            <span
              className={`${officePolicyStyles["action-link-draft"]} ${lockAcknowledgementApproverFields ? "disable-btns" : ""}`}
              onClick={() => handleSubmit(true)}
              role="button"
              tabIndex={0}
            >
              {t("officePolicyManagement.saveAsDraft")}
            </span>
          </div>
        </div>
      </main>

      {categoryModalOpen && (
        <SelectCategoryModal
          isOpen={categoryModalOpen}
          closeModal={() => setCategoryModalOpen(false)}
          categories={categoryList}
          selectedCategory={selectedCategory}
          onSelectCategory={setSelectedCategory}
          onApply={handleCategoryApply}
          accountOwnerId={accountOwnerId}
          refetchCategories={refetchCategories}
        />
      )}

      {officesModalOpen && (
        <SelectOfficesModal
          isOpen={officesModalOpen}
          closeModal={() => setOfficesModalOpen(false)}
          ownerId={accountOwnerId}
          selectedOfficeIds={selectedOffices}
          onSelectionChange={setSelectedOffices}
          onApply={handleOfficesApply}
        />
      )}

      {staffModalOpen && (
        <SelectEmployeeForPolicyVersionModal
          t={t}
          issaveEmployeeModalOpen={staffModalOpen}
          setIsSaveEmployeeModalOpen={() => setStaffModalOpen(false)}
          memberIds={assigneesForModal}
          setMemberIds={setAssignees}
          policyFolderId={folderIdDecoded}
          ownerId={accountOwnerId}
          policyId={policyIdDecoded}
          isManagerOnly={false}
          officeIds={selectedOfficeList}
        />
      )}

      {managerModalOpen && (
        <SelectEmployeeForPolicyVersionModal
          t={t}
          issaveEmployeeModalOpen={managerModalOpen}
          setIsSaveEmployeeModalOpen={() => setManagerModalOpen(false)}
          memberIds={selectedManagerUsers}
          setMemberIds={setSelectedManagerUsers}
          policyFolderId={folderIdDecoded}
          ownerId={accountOwnerId}
          policyId={policyIdDecoded}
          isManagerOnly={true}
          officeIds={selectedOfficeList}
        />
      )}

      <SelectPolicyApproverModal
        isOpen={selectApproverModalOpen}
        toggle={() => setSelectApproverModalOpen(false)}
        staffList={approverStaffListForModal}
        selectedApprover={selectedApprover}
        onSelect={(approver) => {
          setSelectedApprover(approver);
          setErrors((prev) => ({ ...prev, approver: undefined }));
        }}
      />
    </div>
  );
};

export default withTranslation()(AddnewPolicyDocument);
